"use client";
import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Check, ShoppingCart, MessageCircle, Package, Ship, Globe } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { QuickInquiryDialog } from '@/components/QuickInquiryDialog'
import { useQuoteStore } from '@/stores/quoteStore'
import { cn } from '@/lib/utils'
import type { Product, ProductVariant } from '@/types'

const COLOUR_MAP: Record<string, string> = {
  Silver: '#fff',
  Chrome: '#fff',
  Gold: '#fff',
  Black: '#fff',
  Galvanised: '#fff',
  Antique: '#fff',
  Nickel: '#fff',
  Mirror: '#fff',
  'Rose Gold': '#fff',
  Copper: '#fff',
}
// {
//   Silver: '#C0C0C0',
//   Chrome: '#BEC2CB',
//   Gold: '#D4AF37',
//   Black: '#2D2D2D',
//   Galvanised: '#8A9A8A',
//   Antique: '#8B7355',
//   Nickel: '#A19D94',
//   Mirror: '#E8E8E8',
//   'Rose Gold': '#E0BFB8',
//   Copper: '#B87333',
// }

interface Props {
  product: Product
  activeVariant: ProductVariant | null
  sizeVariants: ProductVariant[]
  colorVariants: ProductVariant[]
  categoryName: string
  onHoverImage: (url: string | null) => void
}

export function ProductInfo({
  product,
  activeVariant,
  sizeVariants,
  colorVariants,
  categoryName,
  onHoverImage
}: Props) {
  const router = useRouter()
  const addItem = useQuoteStore((s) => s.addItem)
  const items = useQuoteStore((s) => s.items)
  const [inquiryOpen, setInquiryOpen] = useState(false)
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set())

  const inQuote = !!activeVariant && items.some((i) => i.variantId === activeVariant.id)

  function handleAddToQuote() {
    if (!activeVariant || !product) return
    addItem({
      variantId: activeVariant.id,
      sku: activeVariant.sku,
      productName: product.name,
      size: activeVariant.size,
      material: activeVariant.material,
      finish: activeVariant.finish,
      imageUrl: activeVariant.image_url,
    })
    toast.success(`${product.name} added to quote`)
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <p className="text-xs text-brand-accent font-semibold tracking-[0.2em] uppercase">
            {categoryName}
          </p>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
          {(() => {
            if (!activeVariant) return product.name;
            const skuBase = activeVariant.sku.split('-').slice(0, 2).join('-'); // e.g. JM-201
            const size = activeVariant.size ? `${activeVariant.size.replace('"', ' Inch')} Size` : '';
            const colour = activeVariant.colour ?? activeVariant.finish ?? '';
            const colourPart = colour ? `${colour} Color` : '';
            const parts = [`${skuBase} ${categoryName}`];
            if (size) parts.push(size);
            if (colourPart) parts.push(colourPart);
            return parts.join(', ');
          })()}
        </h1>
        {activeVariant && (
          <p className="text-sm text-muted-foreground font-mono">SKU: {activeVariant.sku}</p>
        )}
      </div>

      <p className="text-base text-muted-foreground leading-relaxed">
        {activeVariant?.description || product.description}
      </p>

      {product.variants && product.variants.length > 1 && (
        <div className="space-y-6">
          {/* Finish Selection */}
          {sizeVariants.length > 1 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                Finish — <span className="text-foreground font-medium normal-case tracking-normal">
                  {activeVariant?.colour} · {activeVariant?.finish}
                </span>
              </p>
              <div className="flex gap-2 flex-wrap min-h-[40px]">
                {sizeVariants.map((variant) => {
                  const isActive = activeVariant?.colour === variant.colour
                  return (
                    <button
                      key={variant.id}
                      onClick={() => {
                        if (variant.slug_url) router.replace(`/product${variant.slug_url}`)
                      }}
                      onMouseEnter={() => { if (variant.image_url) onHoverImage(variant.image_url) }}
                      onMouseLeave={() => onHoverImage(null)}
                      className={cn(
                        "w-16 h-16 rounded-lg border-2 transition-all hover:scale-110 flex items-center justify-center overflow-hidden relative bg-white",
                        isActive ? "border-brand-accent scale-105" : "border-border hover:border-brand-accent/30"
                      )}
                      style={{ backgroundColor: COLOUR_MAP[variant.colour] ?? '#f3f4f6' }}
                    >
                      {variant.image_url && !brokenImages.has(variant.image_url) && (
                        <img
                          src={variant.image_url}
                          alt={variant.colour}
                          className="h-full w-full object-contain relative z-10"
                          onError={() => setBrokenImages(prev => new Set(prev).add(variant.image_url!))}
                        />
                      )}
                      {/* <div className={cn(
                        "absolute inset-0 z-20 flex items-center justify-center transition-all",
                        isActive ? "bg-black/20 opacity-100" : "bg-black/0 opacity-0 group-hover:opacity-100"
                      )}>
                        {isActive && <Check className="h-5 w-5 text-white drop-shadow-md" />}
                      </div> */}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Size Selection */}
          {colorVariants.length > 1 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                Size — <span className="text-foreground font-medium normal-case tracking-normal">
                  {activeVariant?.size}
                </span>
              </p>
              <div className="flex gap-2 flex-wrap min-h-[40px]">
                {colorVariants
                  .sort((a, b) => (parseInt(a.size) || 0) - (parseInt(b.size) || 0))
                  .map((v) => {
                    const isActive = activeVariant?.id === v.id
                    return (
                      <button
                        key={v.id}
                        onClick={() => {
                          if (v.slug_url) router.replace(`/product${v.slug_url}`)
                        }}
                        onMouseEnter={() => { if (v.image_url) onHoverImage(v.image_url) }}
                        onMouseLeave={() => onHoverImage(null)}
                        className={cn(
                          "px-4 py-2 rounded-lg border-2 transition-all font-medium text-sm",
                          isActive
                            ? "border-brand-accent bg-brand-accent/5 text-brand-accent"
                            : "border-border hover:border-muted-foreground/30 bg-muted/30"
                        )}
                      >
                        {v.size}
                      </button>
                    )
                  })}
              </div>
            </div>
          )}
        </div>
      )}

      {activeVariant && (
        <div className="flex flex-wrap gap-2 pt-2">
          <Badge variant="secondary" className="px-3 py-1 font-medium text-foreground/80">{activeVariant.material}</Badge>
          <Badge variant="secondary" className="px-3 py-1 font-medium text-foreground/80">{activeVariant.finish}</Badge>
          <Badge className="bg-brand-accent text-white border-0 px-3 py-1">
            {activeVariant.stock_status === 'available' ? 'Ready to Ship' : 'Made to Order'}
          </Badge>
        </div>
      )}

      <Separator />

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          className="w-full sm:flex-1 rounded-md bg-[#0f172a] hover:bg-[#0f172a]/90 text-white font-semibold h-11 sm:h-12 transition-all hover:-translate-y-0.5"
          onClick={handleAddToQuote}
          disabled={inQuote}
        >
          {inQuote ? <Check className="mr-2 h-4 w-4" /> : <ShoppingCart className="mr-2 h-4 w-4" />}
          {inQuote ? 'Added to Quote' : 'Add to Quote'}
        </Button>
        <Button
          variant="outline"
          className="w-full sm:flex-1 rounded-md font-semibold h-11 sm:h-12 border-border/80 text-foreground hover:bg-muted/50 transition-all hover:-translate-y-0.5"
          onClick={() => setInquiryOpen(true)}
        >
          <MessageCircle className="mr-2 h-4 w-4" />
          Export Inquiry
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-muted/40 divide-y divide-border">
        {[
          { icon: Package, title: "Bulk / container-load orders", desc: "MOQ confirmed in quotation" },
          { icon: Ship, title: "FOB · CIF · DDP", desc: "dispatch from Mundra / Nhava Sheva" },
          { icon: Globe, title: "USD · EUR · GBP · AED · INR", desc: "quoted in your preferred currency" },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3 p-4">
            <item.icon className="h-4 w-4 text-brand-accent flex-shrink-0" />
            <div className="text-sm">
              <span className="font-medium text-foreground/90">{item.title}</span>
              <span className="text-muted-foreground"> — {item.desc}</span>
            </div>
          </div>
        ))}
      </div>

      {inquiryOpen && (
        <QuickInquiryDialog
          open={inquiryOpen}
          onOpenChange={setInquiryOpen}
          productName={product.name}
          productSku={activeVariant?.sku ?? ''}
        />
      )}
    </div>
  )
}
